import React, { useState, useEffect } from "react";
import ProfileInfo from "../../components/Profile/ProfileInfo/ProfileInfo";
import ProfileMenu from "../../components/Profile/ProfileMenu/ProfileMenu";
import CreatePin from "../../components/Auth/CreatePin/CreatePin";
import ChangePin from "../../components/Auth/ChangePin/ChangePin";
import RemovePin from "../../components/Auth/RemovePin/RemovePin";
import { useTelegram } from "../../hooks/useTelegram";
import { useProfile } from "../../hooks/useProfile";
import { handleApiError, authAPI, userAPI } from "../../services/api";

import { useTranslation } from "react-i18next";

const ProfilePage = ({ onLogout }) => {
  const { i18n } = useTranslation();

  const { user, showAlert, showConfirm, hapticFeedback } = useTelegram();
  const { profile, loading: profileLoading, error } = useProfile();
  const [showCreatePin, setShowCreatePin] = useState(false);
  const [showChangePin, setShowChangePin] = useState(false);
  const [showRemovePin, setShowRemovePin] = useState(false);
  const [loading, setLoading] = useState(false);
  const [hasPinCode, setHasPinCode] = useState(false);
  const [currentPinCode, setCurrentPinCode] = useState(null);

  useEffect(() => {
    checkPinStatus();
  }, []);

  const checkPinStatus = async () => {
    try {
      const response = await authAPI.checkTelegramExist();
      const pinValue = response.data.pin_code;
      const shouldRequirePin =
        pinValue &&
        pinValue !== "null" &&
        pinValue !== null &&
        pinValue.toString().trim() !== "" &&
        pinValue.toString().trim() !== "null";

      setHasPinCode(shouldRequirePin);
      setCurrentPinCode(pinValue);
    } catch (error) {
      console.error("Pin status check error:", error);
    }
  };

  const handleCreatePin = async (formData) => {
    setLoading(true);
    hapticFeedback("impact");

    try {
      if (formData.new_pin.length === 4) {
        const oldPinForApi =
          !currentPinCode || currentPinCode === "null" ? null : currentPinCode;
        const response = await authAPI.changePincode(
          formData.new_pin,
          oldPinForApi
        );

        if (response.data.changed === true) {
          showAlert("PIN-код успешно создан!");
          setShowCreatePin(false);
          await checkPinStatus();
          return true;
        } else {
          showAlert("Не удалось создать PIN-код. Попробуйте еще раз.");
          return false;
        }
      }
    } catch (err) {
      showAlert(err?.response?.data?.error);
    } finally {
      setLoading(false);
    }
    return false;
  };

  const handleChangePin = async (action) => {
    console.log('handleChangePin вызван с действием:', action);
  
    // Защита от повторных запросов
    if (loading) {
      console.log('⏳ Запрос уже выполняется, пропускаем...');
      return false;
    }
  
    // Отправка внутреннего OTP (без ввода номера)
    if (action.type === "request_internal_otp") {
      console.log('Отправляем внутренний OTP...');
      setLoading(true);
      hapticFeedback("impact");
  
      try {
        const response = await userAPI.resetPincodeInit();
        console.log('Ответ requestInternalOTP:', response.data);
        
        const { sended } = response.data;
        
        if (sended) {
          console.log('✅ OTP успешно отправлен');
          return true; // Успех - переходим к следующему шагу
        } else {
          console.log('❌ OTP не отправлен');
          showAlert("Не удалось отправить СМС-код");
          return false;
        }
      } catch (err) {
        console.error('❌ Ошибка при отправке OTP:', err);
        showAlert(err?.response?.data?.error);
        return false;
      } finally {
        setLoading(false);
      }
    }
  
    // Подтверждение смены PIN-кода с OTP
    if (action.type === "confirm_change") {
      console.log('Подтверждаем смену PIN с данными:', action.data);
      setLoading(true);
      hapticFeedback("impact");
  
      try {
        // Используем обычный метод смены PIN (бэкенд сам обрабатывает OTP)
        const response = await authAPI.changePincode(
          action.data.new_pin,
          currentPinCode
        );
  
        console.log('Ответ смены PIN:', response.data);
  
        if (response.data.changed === true) {
          console.log('✅ PIN успешно изменен');
          await checkPinStatus();
          showAlert("PIN-код успешно изменен!");
          setShowChangePin(false);
          return true;
        } else {
          console.log('❌ PIN не изменен:', response.data);
          showAlert("Не удалось изменить PIN-код. Проверьте код из SMS.");
          return false;
        }
      } catch (error) {
        console.error('❌ Ошибка при смене PIN:', error);
        showAlert(
          "Ошибка при изменении PIN-кода: " +
            (error?.response?.data?.error || '')
        );
        return false;
      } finally {
        setLoading(false);
      }
    }
  
    console.log('⚠️ Неизвестный тип действия:', action.type);
    return false;
  };
  



  const handleRemovePin = async () => {
    setLoading(true);
    hapticFeedback("impact");

    try {
      const removeResponse = await authAPI.changePincode(
        null,
        currentPinCode
      );

      if (removeResponse.data.changed === true) {
        await checkPinStatus();
        showAlert("PIN-код успешно удален!");
        setShowRemovePin(false);
      } else {
        showAlert("Не удалось удалить PIN-код. Попробуйте еще раз.");
      }
    } catch (error) {
      showAlert(
        "Ошибка при удалении PIN-кода: " +
          (error?.response?.data?.error || '')
      );
    } finally {
      setLoading(false);
    }
  };

  const handleSkipPin = () => {
    setShowCreatePin(false);
    hapticFeedback("selection");
  };

  const handleCancelChangePin = () => {
    setShowChangePin(false);
    hapticFeedback("selection");
  };

  const handleCancelRemovePin = () => {
    setShowRemovePin(false);
    hapticFeedback("selection");
  };

  const handleMenuItemClick = (item) => {
    hapticFeedback("selection");

    switch (item.id) {
      case "settings":
        showAlert("Настройки будут доступны в следующем обновлении");
        break;

      case "security":
        showAlert("Раздел безопасности будет доступен в следующем обновлении");
        break;

      case "create-pin":
        setShowCreatePin(true);
        break;

      case "ru-lang":
        i18n.changeLanguage("ru");
        userAPI.changeUserLang("ru")
        break;

      case "uz-lang":
        i18n.changeLanguage("uz");
        userAPI.changeUserLang("uz")
        break;

      case "en-lang":
        i18n.changeLanguage("en");
        userAPI.changeUserLang("en")
        break;

      case "change-pin":
        setShowChangePin(true);
        break;

      case "remove-pin":
        setShowRemovePin(true);
        break;

      case "support":
        if (window.Telegram?.WebApp) {
          window.Telegram.WebApp.openTelegramLink(
            "https://t.me/tcrypto_sh"
          );
        } else {
          showAlert("Свяжитесь с поддержкой: @Tcrypto_Sh");
        }
        break;

      case "logout":
        if (onLogout) {
          onLogout();
        } else {
          window.location.reload();
        }
        break;

      default:
        console.log("Неизвестное действие:", item.id);
    }
  };

  if (showCreatePin) {
    return (
      <CreatePin
        onCreatePin={handleCreatePin}
        onSkip={handleSkipPin}
        loading={loading}
      />
    );
  }


  if (showChangePin) {
    return (
      <ChangePin
        onChangePin={handleChangePin}  // ← Убедитесь что эта функция передается
        onCancel={handleCancelChangePin}
        loading={loading}
      />
    );
  }

  if (showRemovePin) {
    return (
      <RemovePin
        onRemovePin={handleRemovePin}
        onCancel={handleCancelRemovePin}
        loading={loading}
      />
    );
  }

  return (
    <div className="w-full animate-fadeIn">
      <ProfileInfo
        user={user}
        profile={profile}
        loading={profileLoading}
        error={error}
      />
      <ProfileMenu
        onMenuItemClick={handleMenuItemClick}
        hasPinCode={hasPinCode}
      />
    </div>
  );
};

export default ProfilePage;
